// RITA-1552 — Fenster „Unterschriebenen Vertrag hochladen“ (Skizze Rücklauf vom 21.10.2026, Bild C; F2, F3 und
// Entscheidung E1 von Julia freigegeben). Ein kleines Fenster wie „Systemzugänge › Beantragen“ (Kopf wie beim Fenster
// „Vertrag erstellen“). Von oben nach unten: die Auswahl „Zu welchem Vertrag gehört der Scan?“ (alle im PRM gedruckten
// Verträge, neueste zuerst, mit grauem Zusatz; vorausgewählt der neueste nicht ersetzte ohne Scan, sonst der neueste –
// Review-Befund F2, Freigabe Julia 23.10.2026; Regel in services/vertragsbloecke.ts), bei einem schon vorhandenen Scan
// zum gewählten Vertrag der Hinweis b), das Dateifeld (react-dropzone, nur PDF bis 20 MB), die gewählte Datei
// mit Größe und „✕ entfernen“, darunter der Satz, unter welchem Namen der Scan unter „Dokumente“ liegt (E1). Unten
// rechts „Abbrechen“ und das grüne „Hochladen“, grau bis eine PDF gewählt ist.
//
// Abgelehnte Dateien kommen aus den fileRejections von react-dropzone (Kritik zum Bauplan): falscher Typ, leer oder
// mehrere → „Bitte eine PDF-Datei wählen.“, zu groß → „Die Datei ist größer als 20 MB.“ – direkt unter dem Feld. Der
// Server prüft zusätzlich (Signatur %PDF-, 20 MB); seine Codes NOT_PDF, EMPTY_FILE und FILE_TOO_LARGE ergeben dieselben
// Texte, alles andere „Der Scan konnte nicht hochgeladen werden. Bitte erneut versuchen.“ Das Fenster bleibt dann offen.
// Während des Hochladens heißt der Knopf „Wird hochgeladen …“; beide Knöpfe, das X, Escape, die Auswahl und das
// Dateifeld sind gesperrt. Das Dateifeld ist für Tastatur und Screenreader ein Knopf mit dem Feldtitel als Namen und
// „Datei hierher ziehen oder auswählen“ als Beschreibung (Abschlussreview MR 2, Technik T3). Nach dem Erfolg lädt Apollo
// die Liste „Dokumente“ mit der Berater-UUID aus der Antwort neu; die Karte schließt das Fenster und lädt Protokoll und
// Entwürfe neu (onHochgeladen). Die Karte hängt das Fenster nur ein, solange es offen ist.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogContent, IconButton, MenuItem, Select, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useMutation } from '@apollo/client';
import { FileRejection, useDropzone } from 'react-dropzone';
import { graphqlFehlerCode } from '../../templates/services/vertragsvorlageFehler';
import type { Druckantwort, Druckeintrag } from '../api/vertragsdruck';
import { ScanAntwort, ScanVariablen, UPLOAD_SCAN } from '../api/vertragsruecklauf';
import { dokumenteNeuLaden } from '../hooks/useVertragDrucken';
import { letzterScanFuer, vertragsauswahlFuerScan } from '../services/vertragsbloecke';
import {
  dateiablehnungText,
  ENTFERNEN,
  gewaehlteDateiText,
  HOCHLADEN,
  HOCHLADEN_AUSWAEHLEN,
  HOCHLADEN_FELD,
  HOCHLADEN_FRAGE,
  HOCHLADEN_TITEL,
  HOCHLADEN_ZIEHEN,
  hochladenSpeichernAls,
  SCAN_MAX_BYTES,
  scanDateiname,
  scanFehlertextFuerCode,
  vertragAuswahlTeile,
  vertragsblockTitel,
  WIRD_HOCHGELADEN,
  zweiterScanHinweis,
} from '../services/vertragsdruckTexte';
import { ABBRECHEN } from '../services/vertragsentwurfTexte';

const AKZENT = '#4ADE80';
const FELDTITEL = { fontSize: 13, fontWeight: 600, mb: 0.75 } as const;

interface Props {
  /** Gespeicherte Beraternummer. */
  agentId: number;
  /** „Vorname Nachname“ der gespeicherten Angaben – für den Kopf und den Namen des Scans. */
  beraterName: string;
  /** Aktionsprotokoll des Beraters (nur Einträge zu ihm): Auswahl, Zusätze und Hinweis b). */
  eintraege: Druckeintrag[];
  onClose: () => void;
  /** Hochgeladen und eingetragen; die Karte schließt das Fenster und lädt neu. */
  onHochgeladen: (antwort: Druckantwort) => void;
  /** Nach einem Fehler: Eine verloren gegangene, aber erfolgreiche Antwort zeigt sich so im Protokoll (Muster Druck). */
  onProtokollNeuLaden?: () => void;
}

function ScanHochladenDialog({ agentId, beraterName, eintraege, onClose, onHochgeladen, onProtokollNeuLaden }: Props) {
  const auswahl = useMemo(() => vertragsauswahlFuerScan(eintraege), [eintraege]);
  const [gewaehltId, setGewaehltId] = useState<number | null>(auswahl.vorausgewaehlt);
  const [datei, setDatei] = useState<File | null>(null);
  const [dateifehler, setDateifehler] = useState('');
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [fehler, setFehler] = useState('');
  const aktiv = useRef(true);
  useEffect(
    () => () => {
      aktiv.current = false;
    },
    [],
  );

  const [hochladen] = useMutation<ScanAntwort, ScanVariablen>(UPLOAD_SCAN, {
    refetchQueries: ({ data }) => dokumenteNeuLaden(data?.uploadSignedContract?.agent_uuid),
  });

  const gewaehlt = auswahl.optionen.find((option) => option.eintrag.id === gewaehltId)?.eintrag ?? null;
  const letzterScan = gewaehlt ? letzterScanFuer(eintraege, gewaehlt.id) : null;

  const onDrop = (angenommen: File[], abgelehnt: FileRejection[]) => {
    setFehler('');
    if (abgelehnt.length > 0 || angenommen.length === 0) {
      setDatei(null);
      setDateifehler(dateiablehnungText(abgelehnt[0]?.errors[0]?.code));
      return;
    }
    setDatei(angenommen[0]);
    setDateifehler('');
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    multiple: false,
    // Eine leere Datei ist keine PDF (Server: EMPTY_FILE) – abgelehnt mit „Bitte eine PDF-Datei wählen.“
    minSize: 1,
    maxSize: SCAN_MAX_BYTES,
    disabled: laedtHoch,
    onDrop,
  });

  const hochladenGesperrt = laedtHoch || !datei || !gewaehlt;
  const schliessen = laedtHoch ? undefined : onClose;

  const hochladenKlick = async () => {
    if (!datei || !gewaehlt || laedtHoch) return;
    setLaedtHoch(true);
    setFehler('');
    try {
      const { data } = await hochladen({ variables: { agent_id: agentId, entry_id: gewaehlt.id, file: datei } });
      const antwort = data?.uploadSignedContract;
      if (!antwort) throw new Error('Leere Antwort beim Hochladen des Scans');
      // Die Karte hängt das Fenster sofort aus; danach kein Zustand mehr setzen.
      onHochgeladen(antwort);
    } catch (error) {
      if (aktiv.current) {
        setFehler(scanFehlertextFuerCode(graphqlFehlerCode(error)));
        setLaedtHoch(false);
      }
      onProtokollNeuLaden?.();
    }
  };

  return (
    <Dialog
      open
      // Während des Hochladens lässt sich das Fenster nicht schließen (auch nicht per Escape oder Klick daneben).
      onClose={schliessen}
      maxWidth={false}
      aria-labelledby="scan-hochladen-titel"
      data-testid="scan-hochladen-dialog"
      PaperProps={{
        sx: {
          width: 'min(620px, calc(100% - 32px))',
          m: 2,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: `${AKZENT}18`,
          borderBottom: `1px solid ${AKZENT}35`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: AKZENT, display: 'flex' }}>
            <FileUploadOutlinedIcon />
          </Box>
          <Box>
            <Typography id="scan-hochladen-titel" sx={{ fontSize: 17, fontWeight: 700, color: AKZENT }}>
              {HOCHLADEN_TITEL}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {beraterName} &middot; Nr. {agentId}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={schliessen}
          disabled={laedtHoch}
          size="small"
          aria-label="Schließen"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Typography id="scan-vertrag-frage" sx={FELDTITEL}>
          {HOCHLADEN_FRAGE}
        </Typography>
        <Select
          value={gewaehlt ? String(gewaehlt.id) : ''}
          onChange={(event) => setGewaehltId(Number(event.target.value))}
          labelId="scan-vertrag-frage"
          displayEmpty
          fullWidth
          size="small"
          disabled={laedtHoch}
          // Geschlossen nur der Vertrag (Skizze C); die Zusätze stehen in der aufgeklappten Liste.
          renderValue={() => (gewaehlt ? vertragsblockTitel(gewaehlt) : '')}
          SelectDisplayProps={{ 'data-testid': 'scan-vertrag-auswahl' } as React.HTMLAttributes<HTMLDivElement>}
        >
          {auswahl.optionen.map(({ eintrag, zusatz }) => {
            const teile = vertragAuswahlTeile(eintrag, zusatz);
            // data-value = Kennung des Drucks, ausdrücklich gesetzt (der Browser-Test wählt darüber); MUI Select setzt
            // denselben Wert zwar auch selbst, so hängt der Test aber nicht an diesem Bibliotheksdetail.
            return (
              <MenuItem
                key={eintrag.id}
                value={String(eintrag.id)}
                data-value={String(eintrag.id)}
                data-testid="scan-vertrag-option"
              >
                {teile.vertrag}{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {teile.zusatz}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
        {letzterScan && (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="scan-zweiter-hinweis">
            {zweiterScanHinweis(letzterScan.created_at)}
          </Alert>
        )}

        <Typography id="scan-datei-titel" sx={{ ...FELDTITEL, mt: 2 }}>
          {HOCHLADEN_FELD}
        </Typography>
        <Box
          // Ohne eigene Rolle wäre die fokussierbare Fläche „presentation“ ohne Namen; als Knopf öffnet sie per Enter oder
          // Leertaste die Dateiauswahl (react-dropzone).
          // eslint-disable-next-line react/jsx-props-no-spreading -- Ereignisse und Fokus liefert react-dropzone
          {...getRootProps({
            role: 'button',
            'aria-labelledby': 'scan-datei-titel',
            'aria-describedby': 'scan-datei-aufforderung',
            'aria-disabled': laedtHoch || undefined,
          })}
          data-testid="scan-datei-feld"
          sx={{
            border: 2,
            borderStyle: 'dashed',
            borderColor: isDragActive ? AKZENT : 'divider',
            borderRadius: 2.5,
            p: 2.25,
            textAlign: 'center',
            color: 'text.secondary',
            cursor: laedtHoch ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
          }}
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading -- verstecktes Dateifeld von react-dropzone */}
          <input {...getInputProps()} />
          <DescriptionOutlinedIcon fontSize="small" aria-hidden />
          <span id="scan-datei-aufforderung">
            {HOCHLADEN_ZIEHEN} <u>{HOCHLADEN_AUSWAEHLEN}</u>
          </span>
        </Box>
        {dateifehler && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="scan-datei-fehler">
            {dateifehler}
          </Alert>
        )}
        {datei && (
          <Box
            data-testid="scan-datei-gewaehlt"
            sx={{
              mt: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: `${AKZENT}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, fontSize: 14 }}>
              <DescriptionOutlinedIcon fontSize="small" aria-hidden />
              <span data-testid="scan-datei-name">{gewaehlteDateiText(datei.name, datei.size)}</span>
            </Box>
            <Button
              size="small"
              color="inherit"
              startIcon={<CloseIcon />}
              onClick={() => setDatei(null)}
              disabled={laedtHoch}
              data-testid="scan-datei-entfernen"
              sx={{ textTransform: 'none' }}
            >
              {ENTFERNEN}
            </Button>
          </Box>
        )}
        {gewaehlt && (
          <Typography sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }} data-testid="scan-speichern-als">
            {hochladenSpeichernAls(scanDateiname(beraterName, gewaehlt.created_at))}
          </Typography>
        )}
        {fehler && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="scan-hochladen-fehler">
            {fehler}
          </Alert>
        )}
      </DialogContent>
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={schliessen}
          disabled={laedtHoch}
          sx={{ textTransform: 'none' }}
        >
          {ABBRECHEN}
        </Button>
        <Button
          variant="contained"
          startIcon={<FileUploadOutlinedIcon />}
          onClick={() => {
            hochladenKlick().catch(() => undefined);
          }}
          disabled={hochladenGesperrt}
          data-testid="scan-hochladen-bestaetigen"
          sx={{
            textTransform: 'none',
            bgcolor: AKZENT,
            color: '#063b1e',
            '&:hover': { bgcolor: '#22c55e' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          }}
        >
          {laedtHoch ? WIRD_HOCHGELADEN : HOCHLADEN}
        </Button>
      </Box>
    </Dialog>
  );
}

export default ScanHochladenDialog;
